
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer(): JSX.Element {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email) {
      setSubmitMessage('이메일 주소를 입력해주세요');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d62lsruf9l3frqevbkkg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('구독해 주셔서 감사합니다!');
        setEmail('');
      } else {
        const errorText = await response.text();
        console.error('Server error:', response.status, errorText);
        setSubmitMessage('문제가 발생했습니다. 다시 시도해주세요.');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setSubmitMessage('문제가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const companyLinks = ['회사 소개', '팀 소개', '채용', '보도자료', '연락처'];
  const resourceLinks = [
    { name: '블로그', href: 'https://blog.naver.com/project-lab', external: true },
    { name: '자주 묻는 질문', href: '/faq', external: false },
    { name: '인스타그램', href: 'https://www.instagram.com/pro_j_lab?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==', external: true },
    { name: '카카오톡', href: 'https://open.kakao.com/o/skDwS9ih', external: true }
  ];

  return (
    <footer id="contact" className="bg-[#2A2A2A] text-white py-12 sm:py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mb-12 sm:mb-16">
          <div>
            <div className="flex items-center space-x-3 mb-4 sm:mb-6">
              <span className="text-xl font-light tracking-wider"><span className="font-bold">P</span>.collective</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight mb-4 sm:mb-6">
              함께 특별한<br />공간을 만들어갑니다
            </h2>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed mb-6 sm:mb-8">
              당신의 비전을 현실로 만들 준비가 되셨나요? 뉴스레터를 구독하시고 디자인 인사이트, 프로젝트 업데이트, 건축 영감을 받아보세요.
            </p>

            <form onSubmit={handleSubmit} data-readdy-form id="newsletter-form" className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm text-gray-400 mb-2">
                  이메일 주소
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-transparent border-b border-gray-600 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-white transition-colors text-sm"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 sm:px-8 py-2.5 sm:py-3 border border-white text-white rounded-lg hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 text-sm"
              >
                {isSubmitting ? '구독 중...' : '구독하기'}
              </button>
              {submitMessage && (
                <p
                  className={`text-sm ${
                    submitMessage.includes('감사합니다') ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {submitMessage}
                </p>
              )}
            </form>
          </div>

          <div className="flex flex-col justify-end">
            <div className="mb-6 sm:mb-8">
              <h3 className="text-sm font-semibold mb-3 sm:mb-4 tracking-wider">회사</h3>
              <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-2">
                {companyLinks.map((link) => (
                  <a
                    key={link}
                    href="#about"
                    className="text-gray-400 hover:text-white transition-colors cursor-pointer hover:underline text-sm"
                  >
                    {link}
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-3 sm:mb-4 tracking-wider">리소스</h3>
              <div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6 gap-y-2">
                {resourceLinks.map((link) => (
                  link.external ? (
                    <a
                      key={link.name}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors cursor-pointer hover:underline text-sm"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      key={link.name}
                      to={link.href}
                      className="text-gray-400 hover:text-white transition-colors cursor-pointer hover:underline text-sm"
                    >
                      {link.name}
                    </Link>
                  )
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="pt-6 sm:pt-8 border-t border-gray-700 flex flex-col gap-4 sm:gap-0 sm:flex-row items-center justify-between">
          <p className="text-xs sm:text-sm text-gray-400">© 2025 P.collective. All rights reserved.</p>
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
            <span className="text-xs sm:text-sm text-gray-400 flex items-center gap-1.5">
              <i className="ri-map-pin-line text-xs sm:text-sm"></i>
              강원특별자치도 춘천시 공지로 423-1 103호
            </span>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="text-xs sm:text-sm text-gray-400 hover:text-white transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
